import { AnimatePresence, motion } from 'framer-motion';
import { X, Briefcase, Users, PhoneCall, ClipboardList, Megaphone, AlertTriangle } from 'lucide-react';

interface ResponsabiliteVentesBZTelecomModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const responsabilites = [
  {
    icon: Users,
    title: '1. Trouver des leads et activer le réseau de contacts',
    text:
      "Le rôle ventes est d'alimenter activement le pipeline BZ Telecom en identifiant des prospects, en exploitant le réseau de contacts et en ouvrant des conversations qualifiées.",
    livrables: ['Prospects identifiés', 'Réseau activé', 'Opportunités ouvertes'],
  },
  {
    icon: ClipboardList,
    title: '2. Remplir le tableau des leads et faire les suivis',
    text:
      "Chaque lead doit être inscrit, qualifié, mis à jour et relancé. Le tableau des leads devient la source de vérité pour savoir où en est chaque opportunité.",
    livrables: ['Tableau de leads à jour', 'Suivis documentés', 'Prochaines actions claires'],
  },
  {
    icon: Briefcase,
    title: '3. Aider le client BZ Telecom dans ses services auprès de nous',
    text:
      "Le rôle ventes ne s'arrête pas à la signature. Il faut accompagner le client dans ses demandes, faciliter la coordination avec BZ et s'assurer qu'il se sente bien pris en charge.",
    livrables: ['Demandes bien relayées', 'Client accompagné', 'Coordination fluide avec les équipes'],
  },
  {
    icon: PhoneCall,
    title: '4. En cas de panne, s’occuper du lien client',
    text:
      "Lors d'un incident, ventes devient le point de contact relationnel: rassurer le client, transmettre les informations utiles, faire les suivis et maintenir la confiance pendant que l'équipe technique traite la panne.",
    livrables: ['Client informé', 'Suivis de communication faits', 'Confiance protégée durant la panne'],
  },
  {
    icon: Megaphone,
    title: '5. Faire le suivi auprès des Google Ads / Marketing',
    text:
      "L'équipe ventes doit suivre la performance marketing, remonter la qualité des leads issus des campagnes et s'assurer que Google Ads et les actions marketing servent réellement les objectifs de BZ Telecom.",
    livrables: ['Retour terrain sur la qualité des leads', 'Suivi campagnes / marketing', 'Ajustements proposés'],
  },
];

export default function ResponsabiliteVentesBZTelecomModal({
  isOpen,
  onClose,
}: ResponsabiliteVentesBZTelecomModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 20 }}
          className="relative w-[92vw] max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl border border-amber-500/30 bg-slate-950 shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-amber-500/20 bg-slate-950/95 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg">
                <Briefcase className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-amber-300">
                  Ventes BZ Telecom
                </p>
                <h2 className="text-lg font-bold text-white md:text-xl">
                  Responsabilité des ventes chez BZ Telecom
                </h2>
                <p className="mt-0.5 text-xs text-slate-300/80">
                  Clarifier le rôle ventes pour mieux structurer les leads, les suivis, la relation client et le marketing.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-white/20 bg-white/5 p-2 text-white transition hover:bg-white/10"
              aria-label="Fermer la responsabilité des ventes BZ Telecom"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-[calc(90vh-88px)] overflow-y-auto bg-slate-950/95 p-6">
            <div className="mb-6 rounded-2xl border border-amber-500/20 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wide text-amber-300">Intention</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-200">
                    Cette tuile sert à rendre explicite ce que l’équipe ventes doit porter dans l’écosystème BZ Telecom.
                    Le but est d’éviter les zones grises, les suivis oubliés et les clients laissés entre deux chaises.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {responsabilites.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-slate-700/80 bg-slate-900/70 p-5 shadow-lg"
                  >
                    <div className="mb-3 flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-300">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-white">{item.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-slate-300">{item.text}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {item.livrables.map((livrable) => (
                        <span
                          key={livrable}
                          className="rounded-full bg-amber-500/10 px-3 py-1 text-[11px] font-semibold text-amber-200"
                        >
                          {livrable}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 rounded-2xl border border-slate-700 bg-slate-900/70 p-5">
              <h3 className="text-sm font-bold uppercase tracking-wide text-orange-300">
                Résultat attendu
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                Une équipe ventes qui génère des leads, documente ses suivis, accompagne le client dans la durée,
                protège la relation en cas de panne et boucle la boucle avec le marketing pour améliorer la qualité
                des opportunités. Bref, une équipe qui vend pour vrai et qui assume la relation, ce qui reste un
                concept utile dans le commerce moderne.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
